import { h, FunctionalComponent, Fragment } from "preact";
import { Product } from "../types";
import ProductPrice from "./ProductPrice";
import React from "preact/compat";
import { isAuthorized } from "@helpers/utils";
import { useEffect } from "preact/hooks";

type AccessRestriction = "all" | "efp" | "pb" | "efp_pb";

interface ProductCardProps {
  columns: number;
  product: Product;
}

const ProductCard: FunctionalComponent<ProductCardProps> = ({
  columns,
  product,
}) => {
  const baseClass = columns === 1 ? "product-card-stacked" : "product-card";
  const cardClass = !isAuthorized(product, window.themeVars.customer)
    ? `${baseClass} ${baseClass}--locked`
    : baseClass;

  // Check if this is a single variant out-of-stock product
  const isSingleVariantOutOfStock =
    !product.available && product.tags?.includes("single-variant-out-of-stock");

  const hasBisEnabled =
    isSingleVariantOutOfStock && product.tags.includes("back-in-stock");

  const finalCardClass = isSingleVariantOutOfStock
    ? `${cardClass} ${baseClass}--single-variant-out-of-stock`
    : cardClass;

  let imageClass = "";
  const hidePrice = product.hidePricePlp === "true";

  if (product.images.length === 1) {
    imageClass = `${baseClass}__images--single`;
  }

  if (isSingleVariantOutOfStock) {
    imageClass += ` ${baseClass}__images--out-of-stock`;
  }

  let personalised = false;
  let detailsBtnText = window.themeVars.strings.product.btnDetails;
  if (product.tags.includes("personalised")) {
    personalised = true;
    detailsBtnText = window.themeVars.strings.product.btnPersonalise;
  }

  let showEfpBadge = true;
  if (product.variants.every((variant) => variant.real_compare_at_price)) {
    showEfpBadge = false;
  }

  const getProductLockText = (accessRestriction: AccessRestriction) => {
    const restrictions = window.themeVars.strings.customer.restrictions;
    if (!restrictions) {
      return;
    }
    const restrictionText =
      restrictions[accessRestriction] ??
      window.themeVars.strings.customer.restrictions.default.title;
    return restrictionText.title;
  };

  const ProductContent = () => (
    <div className={`${baseClass}__content`}>
      <div className={`${baseClass}__images ${imageClass}`}>
        <div className={`${baseClass}__badge-wrapper`}>
          {isSingleVariantOutOfStock && (
            <span
              className={`${baseClass}__badge badge badge-yellow`}
              style={{ backgroundColor: "#FEF3C7", color: "#000000" }}
            >
              {window.themeVars.strings.product.soldOut}
            </span>
          )}
          {product.customBadge &&
            product.customBadge !== "no input" &&
            !isSingleVariantOutOfStock && (
              <span className={`${baseClass}__badge badge badge-orange`}>
                {product.customBadge}
              </span>
            )}
          {product.saleBadge && !isSingleVariantOutOfStock && (
            <span className={`${baseClass}__badge badge badge-red`}>
              {product.saleBadge}
            </span>
          )}
        </div>
        {product.landscapeImg && columns === 1 ? (
          <div
            className={`responsive-image ${baseClass}__image ${baseClass}__landscape-image`}
          >
            <a
              className="product-card__link"
              href={`${window.Shopify.routes.root}products/${product.handle}`}
            >
              <img
                width={315}
                height={315}
                src={product.landscapeImg}
                alt={product.title}
              />
            </a>
          </div>
        ) : (
          <React.Fragment>
            <div className={`responsive-image ${baseClass}__image`}>
              <a
                className="product-card__link"
                href={`${window.Shopify.routes.root}products/${product.handle}`}
              >
                <img
                  width={315}
                  height={315}
                  src={product.images[0]?.src}
                  alt={product.title}
                />
              </a>
            </div>
            {product.images.length > 1 && (
              <div
                className={`responsive-image ${baseClass}__image ${baseClass}__image--rollover`}
              >
                <a
                  className="product-card__link"
                  href={`${window.Shopify.routes.root}products/${product.handle}`}
                >
                  <img
                    width={315}
                    height={315}
                    src={product.images[1]?.src}
                    alt={`${product.title} rollover`}
                  />
                </a>
              </div>
            )}
          </React.Fragment>
        )}

        {!isAuthorized(product, window.themeVars.customer) && (
          <div className="product-lock" aria-hidden="true">
            <div className="product-lock__wrapper">
              <div className="product-lock__content">
                <span
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{
                    __html: window.themeVars.icons.lock,
                  }}
                ></span>
                <div className="product-lock__text">
                  <p className="product-lock__title">
                    {getProductLockText(
                      product.accessRestriction as AccessRestriction,
                    )}
                  </p>
                  <p className="product-lock__desc">
                    <a href={window.themeVars.strings.product.logInLink}>
                      {window.themeVars.strings.product.logIn}
                    </a>
                    &nbsp;
                    {window.themeVars.strings.product.lockedText}
                    &nbsp;
                    <a href={window.themeVars.strings.product.signUpLink}>
                      {window.themeVars.strings.product.signUp}
                    </a>
                  </p>
                </div>
              </div>
              <div className="product-lock__buttons">
                <a
                  href={window.themeVars.strings.product.logInLink}
                  className="product__log-in btn btn-white btn-white--outline"
                >
                  {window.themeVars.strings.product.logIn}
                </a>
                <a
                  href={window.themeVars.strings.product.signUpLink}
                  className="product__sign-up btn btn-white btn-white--outline"
                >
                  {window.themeVars.strings.product.signUp}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      {(product.abv ||
        product.style ||
        (product.size && String(product.size) !== "0")) && (
        <div className={`${baseClass}__info outside`}>
          <ul
            className={`
            ${baseClass}__info-wrapper
            ${baseClass}__info-wrapper--${
              [
                product.abv && 1,
                product.style && 1,
                product.size && String(product.size) !== "0" && 1,
              ].filter(Boolean).length
            }-items`}
          >
            {product.abv && (
              <li className={`${baseClass}__info-item`}>
                <span className={`${baseClass}__info-label`}>
                  {window.themeVars.strings.product.details.abv}
                </span>
                <span className={`${baseClass}__info-value`}>
                  {product.abv}%
                </span>
              </li>
            )}
            {product.style && (
              <li className={`${baseClass}__info-item`}>
                <span className={`${baseClass}__info-label`}>
                  {window.themeVars.strings.product.details.style}
                </span>
                <span className={`${baseClass}__info-value`}>
                  {product.style}
                </span>
              </li>
            )}
            {product.size && String(product.size) !== "0" && (
              <li className={`${baseClass}__info-item`}>
                <span className={`${baseClass}__info-label`}>
                  {window.themeVars.strings.product.details.size}
                </span>
                <span className={`${baseClass}__info-value`}>
                  {product.size}
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
      <div className={`${baseClass}__details-buttons`}>
        <div className={`${baseClass}__details-wrapper`}>
          <div className={`${baseClass}__details`}>
            <div className={`${baseClass}__wrapper`}>
              <a
                className="product-card__link"
                href={`${window.Shopify.routes.root}products/${product.handle}`}
              >
                <h3
                  className={`${baseClass}__title heading-4`}
                  dangerouslySetInnerHTML={{
                    __html: product.title,
                  }}
                ></h3>
              </a>
              <p className={`${baseClass}__subtitle text-md-body`}>
                {product.subtitle}
              </p>
              {(product.abv ||
                product.style ||
                (product.size && String(product.size) !== "0")) && (
                <div className={`${baseClass}__info inside`}>
                  <ul
                    className={`
                    ${baseClass}__info-wrapper
                    ${baseClass}__info-wrapper--${
                      [
                        product.abv && 1,
                        product.style && 1,
                        product.size && String(product.size) !== "0" && 1,
                      ].filter(Boolean).length
                    }-items`}
                  >
                    {product.abv && (
                      <li className={`${baseClass}__info-item`}>
                        <span className={`${baseClass}__info-label`}>
                          {window.themeVars.strings.product.details.abv}
                        </span>
                        <span className={`${baseClass}__info-value`}>
                          {product.abv}%
                        </span>
                      </li>
                    )}
                    {product.style && (
                      <li className={`${baseClass}__info-item`}>
                        <span className={`${baseClass}__info-label`}>
                          {window.themeVars.strings.product.details.style}
                        </span>
                        <span className={`${baseClass}__info-value`}>
                          {product.style}
                        </span>
                      </li>
                    )}
                    {product.size && String(product.size) !== "0" && (
                      <li className={`${baseClass}__info-item`}>
                        <span className={`${baseClass}__info-label`}>
                          {window.themeVars.strings.product.details.size}
                        </span>
                        <span className={`${baseClass}__info-value`}>
                          {product.size}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {window.themeVars.customer.staff && showEfpBadge ? (
                <p
                  className={`${baseClass}__staff-member-badge staff-member-badge`}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: window.themeVars.icons.checkmark,
                    }}
                  ></span>
                  {window.themeVars.strings.product.staffDiscount}
                </p>
              ) : window.themeVars.customer.efpMember && showEfpBadge ? (
                <p
                  className={`${baseClass}__efp-member-badge efp-member-badge`}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: window.themeVars.icons.checkmark,
                    }}
                  ></span>
                  {window.themeVars.strings.product.efpDiscount}
                </p>
              ) : null}
              {!hidePrice && (
                <p className={`${baseClass}__price`}>
                  <ProductPrice product={product} />
                </p>
              )}
            </div>
          </div>
          <div className={`${baseClass}__buttons`}>
            {personalised === false &&
              (product.stockistButtonLink && product.stockistButtonText ? (
                <a
                  href={product.stockistButtonLink}
                  className={`${baseClass}__options-cta btn btn-black btn-small`}
                  aria-label={`${product.stockistButtonText} ${product.title}`}
                >
                  {product.stockistButtonText}
                </a>
              ) : isSingleVariantOutOfStock ? (
                // For single variant out-of-stock products, show Notify Me or Sold Out button
                hasBisEnabled ? (
                  <button
                    type="button"
                    className={`${baseClass}__options-cta btn btn-black btn-small`}
                    data-bis-trigger
                    data-product={product.id}
                    data-variant={product.variants[0]?.id}
                  >
                    <span>{window.themeVars.strings.product.notifyMe}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${baseClass}__options-cta btn btn-black btn-small`}
                    disabled
                  >
                    <span>{window.themeVars.strings.product.soldOut}</span>
                  </button>
                )
              ) : (
                !hidePrice && (
                  <Fragment>
                    <uwp-quickshop-button
                      data-product={JSON.stringify(product)}
                    >
                      <button
                        aria-label={String(
                          window.themeVars.strings.product.quickshopLabel,
                        ).concat(product.title)}
                        data-quickshop={product.handle}
                        className="product-card__options-cta btn btn-black btn-small"
                      >
                        {product.preOrder != "true"
                          ? window.themeVars.strings.product.btnOptions
                          : window.themeVars.strings.product.pre_order}
                      </button>
                    </uwp-quickshop-button>
                  </Fragment>
                )
              ))}
            <a
              href={`${window.Shopify.routes.root}products/${product.handle}`}
              className={`${baseClass}__details-cta btn btn-transparent btn-small`}
              aria-label={`${window.themeVars.strings.product.linkLabel}${product.title}`}
              {...(personalised && { style: { width: "100%" } })}
            >
              {isSingleVariantOutOfStock
                ? window.themeVars.strings.product.readMore
                : detailsBtnText}
            </a>
          </div>

          {/* Render notify me modal for products that have BIS enabled OR when quickshop is shown */}
          {(hasBisEnabled || !hidePrice) && (
            <uwp-product-notify-me
              data-product-id={product.id}
              data-variant-id=""
              product-data={JSON.stringify(product)}
              is-visible="false"
            />
          )}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const bisButton = document.querySelector(
      `[data-bis-trigger][data-product="${product.id}"]`,
    ) as HTMLElement;

    const bisPopup = document.querySelector(
      `uwp-product-notify-me[data-product-id="${product.id}"]`,
    ) as HTMLElement;

    if (bisButton && bisPopup) {
      const handleBisClick = () => {
        bisPopup.setAttribute("is-visible", "true");
      };

      bisButton.addEventListener("click", handleBisClick);

      return () => {
        bisButton.removeEventListener("click", handleBisClick);
      };
    }
  }, [product.id]);

  return (
    <article
      className={finalCardClass}
      aria-label={`product-title-${product.title}`}
      id={product.handle}
    >
      {!isAuthorized(product, window.themeVars.customer) ? (
        <ProductContent />
      ) : (
        <ProductContent />
      )}
    </article>
  );
};

export default ProductCard;

// Test Products
// Big Bash - pb
// Blind Side - efp
// DDH Citra - efp_pb
// Nitro AF Stout - all
// Wingman Touchdown Lager - none
// Stacked BrewDog T-shirt - unselected

// Log out and confirm you see the "locked" UI for 'all', 'efp', 'pb', 'efp_pb'.
// Log in with an account that does not have the efp-member or pb-member tags. Confirm you see locked for 'efp', 'pb', 'efp_pb'.
// Add the "efp-member" tag to that customer; confirm 'efp' and 'all' are unlocked, 'pb' is still locked, 'efp_pb' is still locked.
// Add the "pb-member" tag; confirm 'pb', 'efp_pb', etc. are now unlocked.
